/**
 * Sources axis of the unified filter architecture (relays and relay sets).
 *
 * A source selection narrows the relays a list *subscribes to*, which is the
 * only way to filter by source that scales: the relay does the work and the
 * `until` cursor stays meaningful. Per-event provenance is not used as a filter
 * because relays routinely deliver the same event and "which relay sent it
 * first" is not a property users can reason about.
 *
 * When none of the selected relays belong to the current list (for example after
 * switching to another relay set) the list falls back to its own relays and the
 * selection is kept: a stale selection must never empty a feed.
 */
export function narrowRelaySources(relays: readonly string[], selected: readonly string[]): string[] {
  if (selected.length === 0) return [...relays]
  const allowed = new Set(selected)
  const narrowed = relays.filter((relay) => allowed.has(relay))
  return narrowed.length > 0 ? narrowed : [...relays]
}

/** True when the selection is meaningful for this list (at least one relay matches). */
export function sourceSelectionApplies(relays: readonly string[], selected: readonly string[]): boolean {
  if (selected.length === 0) return false
  return relays.some((relay) => selected.includes(relay))
}

/**
 * Relay addresses that can be offered by the source filter for a list.
 */
export function sourceOptions(relays: readonly string[]): string[] {
  return [...new Set(relays.filter(Boolean))]
}
